import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { 
  Shield, 
  Heart, 
  GraduationCap, 
  Award, 
  Clock, 
  CheckCircle, 
  ArrowRight,
  Star,
  Phone,
  ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import MarketingLayout from '../../components/marketing/MarketingLayout';
import { siteConfig } from '../../config/site';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

// Counter animation hook
function useCountUp(end, duration = 2000, start = 0) {
  const [count, setCount] = useState(start);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    if (!inView) return;
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * (end - start) + start));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [inView, end, duration, start]);

  return { count, ref };
}

// Hero Section
function HeroSection() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
      </div>

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '64px 64px'
        }}
      />

      <motion.div 
        style={{ y, opacity }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="space-y-8"
        >
          {/* Badge */}
          <motion.div variants={fadeInUp} className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium">
              <Shield className="w-4 h-4" />
              <span>Trusted Medical Coverage Since {siteConfig.company.founded}</span>
            </div>
          </motion.div>

          {/* Main headline */}
          <motion.h1 
            variants={fadeInUp}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white"
          >
            {siteConfig.tagline.split(' ').slice(0, 2).join(' ')}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
              {siteConfig.tagline.split(' ').slice(2).join(' ')}
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p 
            variants={fadeInUp}
            className="max-w-2xl mx-auto text-lg sm:text-xl text-slate-400 leading-relaxed"
          >
            {siteConfig.description}
          </motion.p>

          {/* CTAs */}
          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
          >
            <Button 
              asChild
              size="lg" 
              className="w-full sm:w-auto px-8 py-6 text-base bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg shadow-emerald-500/25 transition-all hover:shadow-emerald-500/40 hover:scale-105"
            >
              <Link to="/contact?type=event-quote" data-testid="hero-quote-btn">
                Request Event Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline" 
              size="lg"
              className="w-full sm:w-auto px-8 py-6 text-base border-slate-700 text-white hover:bg-white/10 rounded-full transition-all hover:scale-105"
            >
              <Link to="/services" data-testid="hero-services-btn">
                Explore Services
              </Link>
            </Button>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 rounded-full border-2 border-slate-600 flex items-start justify-center p-1"
          >
            <div className="w-1.5 h-3 bg-slate-400 rounded-full" />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}

// Trust Strip Section
function TrustStrip() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  const credentials = [
    { icon: Award, label: 'ACLS/ATLS/BLS Certified' },
    { icon: Shield, label: 'Licensed Clinicians' },
    { icon: Clock, label: 'Rapid Response Team' },
    { icon: CheckCircle, label: 'Fully Insured' },
  ];

  return (
    <section ref={ref} className="py-12 bg-slate-900 border-y border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="flex flex-wrap items-center justify-center gap-8 md:gap-16"
        >
          {credentials.map((item, idx) => (
            <motion.div 
              key={idx}
              variants={fadeInUp}
              className="flex items-center gap-3 text-slate-300"
            >
              <item.icon className="w-6 h-6 text-emerald-400" />
              <span className="font-medium">{item.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// Service Pillars Section
function ServicePillars() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const services = [
    {
      icon: Shield,
      title: 'Event Medical Coverage',
      description: 'Professional on-site medical teams for festivals, sports events, corporate gatherings, and private functions. From ALS standby to full medical stations.',
      link: '/services#events',
      color: 'emerald'
    },
    {
      icon: Heart,
      title: 'Private Home Consultations',
      description: 'Personalized medical care in the comfort of your home. Assessments, treatments, and referrals by experienced healthcare professionals.',
      link: '/services#home-visits',
      color: 'cyan'
    },
    {
      icon: GraduationCap,
      title: 'First Aid & Emergency Training',
      description: 'Certified courses in First Aid, CPR, BLS, and AED operation. Equip your team with life-saving skills and confidence.',
      link: '/training',
      color: 'blue'
    }
  ];

  const colorClasses = {
    emerald: {
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/20',
      icon: 'text-emerald-400',
      hover: 'hover:border-emerald-500/40 hover:shadow-emerald-500/10'
    },
    cyan: {
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-500/20',
      icon: 'text-cyan-400',
      hover: 'hover:border-cyan-500/40 hover:shadow-cyan-500/10'
    },
    blue: {
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/20',
      icon: 'text-blue-400',
      hover: 'hover:border-blue-500/40 hover:shadow-blue-500/10'
    }
  };

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p variants={fadeInUp} className="text-emerald-400 font-medium mb-4">
            Our Services
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Comprehensive Medical Solutions
          </motion.h2>
          <motion.p variants={fadeInUp} className="max-w-2xl mx-auto text-slate-400 text-lg">
            Three core service pillars designed to keep your people safe, prepared, and healthy.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8"
        >
          {services.map((service, idx) => {
            const colors = colorClasses[service.color];
            return (
              <motion.div
                key={idx}
                variants={fadeInUp}
                whileHover={{ y: -8, scale: 1.02 }}
                className={`relative p-8 rounded-3xl border ${colors.border} ${colors.hover} bg-slate-900/50 backdrop-blur-sm shadow-2xl transition-all duration-300 group`}
              >
                <div className={`w-14 h-14 rounded-2xl ${colors.bg} flex items-center justify-center mb-6`}>
                  <service.icon className={`w-7 h-7 ${colors.icon}`} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                <p className="text-slate-400 mb-6 leading-relaxed">{service.description}</p>
                <Link 
                  to={service.link}
                  className={`inline-flex items-center gap-2 ${colors.icon} font-medium group-hover:gap-3 transition-all`}
                  data-testid={`service-card-${idx}`}
                >
                  Learn more
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

// Stats Section
function StatsSection() {
  const stats = [
    { label: 'Events Covered', value: 500, suffix: '+' },
    { label: 'Patients Assisted', value: 25000, suffix: '+' },
    { label: 'Staff Trained', value: 3000, suffix: '+' },
    { label: 'Response Ready', value: 24, suffix: '/7' },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-slate-900 to-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, idx) => (
            <StatItem key={idx} stat={stat} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatItem({ stat, idx }) {
  const { count, ref } = useCountUp(stat.value, 2000);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: idx * 0.1 }}
      className="text-center"
    >
      <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2">
        {count.toLocaleString()}{stat.suffix}
      </div>
      <div className="text-slate-400 font-medium">{stat.label}</div>
    </motion.div>
  );
}

// Testimonials Section
function TestimonialsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const testimonials = [
    {
      quote: "Apex Medical Group provided exceptional coverage for our 10,000-person music festival. Their team was professional, responsive, and truly cared about attendee safety.",
      author: "Sarah Mitchell",
      role: "Event Director, SoundWave Festival",
      avatar: "SM"
    },
    {
      quote: "The home consultation service was a lifesaver when my elderly mother needed medical attention. The doctor was thorough, compassionate, and arrived within the hour.",
      author: "James Rodriguez",
      role: "Family Caregiver",
      avatar: "JR"
    },
    {
      quote: "Our corporate team completed the First Aid certification course. The training was engaging, practical, and our employees now feel confident to respond in emergencies.",
      author: "David Chen",
      role: "HR Director, TechFlow Inc.",
      avatar: "DC"
    },
  ];

  return (
    <section ref={ref} className="py-24 bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.p variants={fadeInUp} className="text-emerald-400 font-medium mb-4">
            Testimonials
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-white">
            Trusted by Organizers & Families
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8"
        >
          {testimonials.map((item, idx) => (
            <motion.div
              key={idx}
              variants={fadeInUp}
              className="p-8 rounded-3xl bg-slate-900/50 border border-slate-800 hover:border-slate-700 transition-colors"
            >
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-slate-300 text-lg leading-relaxed mb-6">&ldquo;{item.quote}&rdquo;</p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-semibold">
                  {item.avatar}
                </div>
                <div>
                  <p className="text-white font-medium">{item.author}</p>
                  <p className="text-slate-400 text-sm">{item.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// Final CTA Section
function FinalCTA() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
          className="text-center space-y-8"
        >
          <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-white">
            Let&apos;s Plan Your Coverage
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-xl text-slate-400 max-w-2xl mx-auto">
            Whether you&apos;re organizing an event, need home medical care, or want to train your team — 
            we&apos;re here to help.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              asChild
              size="lg" 
              className="w-full sm:w-auto px-8 py-6 text-base bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg shadow-emerald-500/25 transition-all hover:shadow-emerald-500/40"
            >
              <Link to="/contact?type=event-quote" data-testid="cta-quote-btn">
                Request a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline" 
              size="lg"
              className="w-full sm:w-auto px-8 py-6 text-base border-slate-700 text-white hover:bg-white/10 rounded-full"
            >
              <Link to="/contact" data-testid="cta-contact-btn">
                <Phone className="mr-2 w-5 h-5" />
                Contact Us
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// Main HomePage Component
export default function HomePage() {
  return (
    <MarketingLayout transparentNav>
      <HeroSection />
      <TrustStrip />
      <ServicePillars />
      <StatsSection />
      <TestimonialsSection />
      <FinalCTA />
    </MarketingLayout>
  );
}
